import type { EnvironmentView, Tier } from "@cosmosai/api-client";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { useCosmos } from "../../api/cosmos";
import { environmentsQuery } from "../../api/registry-queries";

/** Where the user works, from the URL: the workspace, and the project when one is chosen. */
export function useCosmosContext(): { workspace: string | null; project: string | null } {
  const params = useParams({ strict: false }) as { workspace?: string; project?: string };
  const search = useSearch({ strict: false }) as { project?: unknown };
  const project =
    params.project ?? (typeof search.project === "string" ? search.project : undefined);
  return { workspace: params.workspace ?? null, project: project ?? null };
}

const tierRank: Record<Tier, number> = { development: 0, staging: 1, production: 2 };

/** Environments by tier (development first), then by name. */
export const byTier = (environments: EnvironmentView[]): EnvironmentView[] =>
  [...environments].sort(
    (a, b) => tierRank[a.tier] - tierRank[b.tier] || a.name.localeCompare(b.name),
  );

/** The workspace's environments by tier and the selected one: `env` in the URL, else the first. */
export function useEnvironment(workspace: string | null) {
  const client = useCosmos();
  const query = useQuery({ ...environmentsQuery(client, workspace ?? ""), enabled: !!workspace });
  const search = useSearch({ strict: false }) as { env?: unknown };
  const environments = byTier(query.data ?? []);
  const current = environments.find((env) => env.name === search.env) ?? environments[0] ?? null;
  return { ...query, environments, current };
}

/** Whether the selected environment is production: what changes an environment is hidden there. */
export function useReadOnly(): boolean {
  const { workspace } = useCosmosContext();
  return useEnvironment(workspace).current?.tier === "production";
}
